import React, { useState, useEffect } from 'react';
import { Tag } from 'lucide-react';
import ProductCard from './ProductCard';
import { Button } from './ui/button';
import axios from 'axios';
import { observeNewElements } from '../utils/scrollAnimations';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const StealsDealsPage = ({ addToCart }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('');

  useEffect(() => {
    fetchProducts();
  }, [sortBy]);

  // Reinitialize scroll animations when products change
  useEffect(() => {
    if (products.length > 0) {
      const timer = setTimeout(() => {
        observeNewElements();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/products?limit=1000`);
      
      // Filter for deals and steals:
      // 1. Products priced at $150 or less
      // 2. Products with low stock (optional additional filter)
      let filteredProducts = response.data.filter(product => {
        // Must be $150 or less
        const isPriceQualified = product.price <= 150;
        
        // Optional: Also show low stock items
        const isLowStock = product.low_stock_threshold && 
                          product.variants.some(v => v.stock_quantity <= product.low_stock_threshold);
        
        return isPriceQualified;
      });

      // Sort products
      if (sortBy === 'price-low') {
        filteredProducts.sort((a, b) => a.price - b.price);
      } else if (sortBy === 'price-high') {
        filteredProducts.sort((a, b) => b.price - a.price);
      } else if (sortBy === 'newest') {
        filteredProducts.reverse();
      }

      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching steals and deals:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-brand-purple text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-up">
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Tag className="w-5 h-5" />
              <span className="text-sm font-medium">Limited Time</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">Steals & Deals</h1>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Heat at $150 or less. Moving fast—lock it in before it's gone.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <span className="font-medium">{products.length} Deals Available</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple"
                >
                  <option value="" disabled>Sort By:</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="newest">Newest First</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-square rounded-2xl mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Deals Right Now</h3>
            <p className="text-gray-600">Check back soon for fresh steals.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={addToCart}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StealsDealsPage;